// frontend-react/src/pages/LoginPage.tsx
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import './LoginPage.css';
import { message, Modal } from 'antd';
import React, { useEffect, useState } from 'react';

// ✅ 引入 reCAPTCHA V3 Hook
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

// ✅ 引入 PasswordExpiredError（讓 LoginPage 能識別密碼過期）
import { PasswordExpiredError } from '../services/authService';

// ✅ ISO 規範：電子信箱遮蔽工具
const maskEmail = (email: string) => {
  if (!email || !email.includes('@')) return email;
  const parts = email.split('@');
  const local = parts[0] || '';
  const domain = parts[1] || '';

  if (local.length <= 3) return `***@${domain}`;
  return `${local.slice(0, -3)}***@${domain}`;
};

const GoogleIcon = () => (
  <svg width='20' height='20' viewBox='0 0 48 48' style={{ marginRight: '0.75rem' }}>
    <path
      fill='#FFC107'
      d='M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z'
    ></path>
    <path
      fill='#FF3D00'
      d='M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z'
    ></path>
    <path
      fill='#4CAF50'
      d='M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z'
    ></path>
    <path
      fill='#1976D2'
      d='M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C42.012 35.24 44 30.023 44 24c0-1.341-.138-2.65-.389-3.917z'
    ></path>
  </svg>
);

const CompanyIcon = () => (
  <img
    src='/images/legalsign-icon.png'
    alt='LegalSign.ai'
    style={{ width: 20, height: 20, marginRight: '0.75rem' }}
  />
);

const SunIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' width='20' height='20'>
    <circle cx='12' cy='12' r='5'></circle>
    <path d='M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42'></path>
  </svg>
);

const MoonIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' width='20' height='20'>
    <path d='M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z'></path>
  </svg>
);

const EyeIcon = () => (
  <svg
    viewBox='0 0 24 24'
    width='20'
    height='20'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'></path>
    <circle cx='12' cy='12' r='3'></circle>
  </svg>
);

const EyeOffIcon = () => (
  <svg
    viewBox='0 0 24 24'
    width='20'
    height='20'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24'></path>
    <line x1='1' y1='1' x2='23' y2='23'></line>
  </svg>
);

const LoginPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);

  const [email, setEmail] = useState(''); // 真實 Email
  const [showEmail, setShowEmail] = useState(false); // 遮罩切換開關
  const [isEmailFocused, setIsEmailFocused] = useState(false); // 游標聚焦判斷

  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // ✅ 忘記密碼相關 State
  const [isForgotModalOpen, setIsForgotModalOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const navigate = useNavigate();
  const { login, register, forgotPassword } = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason');

    if (reason === 'session_invalid') {
      message.warning(
        '登入狀態已失效，請重新登入後再試。\n可能原因：登入逾時／已在其他裝置登入／密碼更新後需重新登入。'
      );

      // 顯示一次後把 query 清掉，避免重新整理又跳一次
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const toggleTheme = () => setIsDarkMode(v => !v);
  const togglePasswordVisibility = () => setShowPassword(v => !v);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let captchaToken = '';
      if (executeRecaptcha) {
        captchaToken = await executeRecaptcha(isLogin ? 'login' : 'register');
        if (!captchaToken) {
          setError('安全驗證失敗，請重試');
          return;
        }
      }

      if (isLogin) {
        await login(email, password, captchaToken);
      } else {
        await register(email, password, name, captchaToken);
      }

      navigate('/');
    } catch (err: any) {
      console.error('Auth error:', err);

      // ✅ 1) 密碼過期：引導去修改密碼頁（你專案路由若不同，改 navigate 的路徑即可）
      if (err instanceof PasswordExpiredError) {
        setLoading(false);

        Modal.warning({
          title: '密碼已過期，請先更新',
          content: err?.message || '密碼已過期（超過規定天數未更新），請先修改密碼後再登入。',
          okText: '前往修改密碼',
          cancelText: '取消',
          onOk: () => {
            // ⚠️ 依你專案路由調整
            navigate('/change-password');
          },
        });

        return;
      }

      // ✅ 2) Single Active Session: 409 -> 詢問是否繼續（force login）
      if (
        err?.detail?.error_code === 'ACTIVE_SESSION_EXISTS' ||
        err?.name === 'ActiveSessionExistsError'
      ) {
        // 先把 loading 放掉，不然按鈕會一直 disabled
        setLoading(false);

        Modal.confirm({
          title: '此帳號已在其他裝置登入',
          content: err?.detail?.message || '繼續登入將會使原本裝置登出。',
          okText: '繼續登入',
          cancelText: '取消',
          onOk: async () => {
            try {
              setLoading(true);

              let captchaToken2 = '';
              if (executeRecaptcha) {
                captchaToken2 = await executeRecaptcha('login');
                if (!captchaToken2) {
                  throw new Error('安全驗證失敗，請重試');
                }
              }

              // ✅ 關鍵：force 登入走 AuthContext，避免 token/user 狀態不同步
              await login(email, password, captchaToken2, true);

              navigate('/');
            } catch (e: any) {
              setError(e?.message || '強制登入失敗');
            } finally {
              setLoading(false);
            }
          },
        });

        return;
      }

      setError(err?.message || '操作失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async () => {
    if (!forgotEmail) {
      message.warning('請輸入註冊的 Email 地址');
      return;
    }

    setForgotLoading(true);
    try {
      let captchaToken = '';
      if (executeRecaptcha) {
        captchaToken = await executeRecaptcha('forgot_password');
      }

      // ✅ 統一走 AuthContext（內部仍會呼叫 authService）
      await forgotPassword(forgotEmail, captchaToken);
    } catch (err: any) {
      // 資安防枚舉：失敗也顯示成功，避免駭客探測帳號是否存在
    } finally {
      setForgotLoading(false);

      Modal.success({
        title: '已寄出重設信件',
        content: '若此帳號存在，系統將會發送重設郵件，請至信箱查收。',
        onOk: () => {
          setIsForgotModalOpen(false);
          setForgotEmail('');
        },
      });
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--color-bg)',
      }}
    >
      <Header minimal />
      <div className={`login-container ${isDarkMode ? 'dark' : ''}`} style={{ flex: 1 }}>
        <div className={`login-card ${isDarkMode ? 'dark' : ''}`}>
          <button
            className='theme-toggle'
            onClick={toggleTheme}
            type='button'
            aria-label={isDarkMode ? '切換到淺色模式' : '切換到深色模式'}
          >
            {isDarkMode ? <SunIcon /> : <MoonIcon />}
          </button>

          <div className='login-header'>
            <h1 className='login-title'>{isLogin ? 'Sign in' : 'Sign up'}</h1>
            <p className='login-subtitle'>
              {isLogin ? 'Welcome back to LegalSign.ai' : 'Join LegalSign.ai'}
            </p>
          </div>

          <div className='social-login'>
            <button
              className='social-btn google-btn'
              disabled
              type='button'
              title='此登入方式目前尚未開放'
            >
              <GoogleIcon /> Continue with Google
            </button>

            <button
              className='social-btn company-btn'
              disabled
              type='button'
              title='此登入方式目前尚未開放'
            >
              <CompanyIcon /> Continue with LegalSign.ai
            </button>
          </div>

          <div className='divider'>
            <span className='divider-text'>or manual login</span>
          </div>

          {error && <div className='error-message'>{error}</div>}

          <form onSubmit={handleSubmit} className='login-form'>
            {!isLogin && (
              <div className='form-group'>
                <label htmlFor='name' className='form-label'>
                  Full Name
                </label>
                <input
                  type='text'
                  id='name'
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className='form-input'
                  required={!isLogin}
                  disabled={loading}
                />
              </div>
            )}

            <div className='form-group'>
              <label htmlFor='email' className='form-label'>
                Email
              </label>
              <div className='password-input-wrapper'>
                <input
                  type='text'
                  id='email'
                  value={isEmailFocused || showEmail ? email : maskEmail(email)}
                  onChange={e => setEmail(e.target.value)}
                  onFocus={() => setIsEmailFocused(true)}
                  onBlur={() => setIsEmailFocused(false)}
                  className='form-input password-input'
                  required
                  disabled={loading}
                  autoComplete='email'
                />
                <button
                  type='button'
                  className='password-toggle-btn'
                  onClick={() => setShowEmail(v => !v)}
                  tabIndex={-1}
                >
                  {showEmail ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <div className='form-group'>
              <label htmlFor='password' className='form-label'>
                Password
              </label>
              <div className='password-input-wrapper'>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id='password'
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className='form-input password-input'
                  required
                  disabled={loading}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                />
                <button
                  type='button'
                  className='password-toggle-btn'
                  onClick={togglePasswordVisibility}
                  disabled={loading}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            {isLogin && (
              <div className='form-options'>
                <button
                  type='button'
                  className='forgot-password'
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#1E88A8',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                  onClick={() => setIsForgotModalOpen(true)}
                >
                  忘記密碼
                </button>
              </div>
            )}

            <button type='submit' className='submit-btn' disabled={loading}>
              {loading
                ? isLogin
                  ? 'Verifying...'
                  : 'Signing up...'
                : isLogin
                  ? 'Sign in'
                  : 'Sign up'}
            </button>

            <div
              style={{ marginTop: '10px', fontSize: '12px', color: '#888', textAlign: 'center' }}
            >
              This site is protected by reCAPTCHA and the Google{' '}
              <a href='https://policies.google.com/privacy' target='_blank' rel='noreferrer'>
                Privacy Policy
              </a>{' '}
              and{' '}
              <a href='https://policies.google.com/terms' target='_blank' rel='noreferrer'>
                Terms of Service
              </a>{' '}
              apply.
            </div>
          </form>

          <div className='auth-switch'>
            <p className='switch-text'>
              {isLogin ? '如需建立帳號，請洽系統管理員或服務窗口。' : 'Already have an account?'}{' '}
              <button type='button' onClick={() => setIsLogin(v => !v)} className='switch-btn'>
                {/* {isLogin ? 'Sign up' : 'Sign in'} */}
              </button>
            </p>
          </div>
        </div>

        {/* ✅ 忘記密碼對話框 (Modal) */}
        <Modal
          title='Reset Your Password'
          open={isForgotModalOpen}
          onOk={handleForgotPasswordSubmit}
          onCancel={() => {
            setIsForgotModalOpen(false);
            setForgotEmail('');
          }}
          confirmLoading={forgotLoading}
          okText='發送重設連結'
          cancelText='取消'
        >
          <div style={{ padding: '10px 0' }}>
            <p>請輸入您的 Email 地址，我們將發送密碼重設連結給您。</p>
            <input
              type='email'
              placeholder='example@email.com'
              value={forgotEmail}
              onChange={e => setForgotEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #d9d9d9',
              }}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default LoginPage;
